import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// Result type for auth operations
interface AuthResult {
  success: boolean;
  error?: string;
}

// Profile type (matches database profiles table)
interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  phone: string | null;
  timezone: string;
  user_preferences: Record<string, unknown>;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;

  // Auth operations
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signInWithMagicLink: (email: string) => Promise<AuthResult>;
  signInWithGoogle: () => Promise<AuthResult>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updatePassword: (newPassword: string) => Promise<AuthResult>;

  // Profile operations
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  // Track explicit sign-out to suppress phantom INITIAL_SESSION events
  const signedOutRef = useRef(false);

  // Sync theme preference from profile to localStorage and document
  const syncThemeFromProfile = useCallback((profileData: Profile | null) => {
    if (!profileData?.user_preferences) return;
    const savedTheme = profileData.user_preferences.theme as string | undefined;
    if (savedTheme === 'light' || savedTheme === 'dark') {
      localStorage.setItem('sapling-theme', savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('[AuthContext] fetchProfile error:', error.message, error.code);
      return null;
    }

    return data as Profile;
  }, []);

  // Refresh profile data (keeps existing profile if fetch fails)
  const refreshProfile = useCallback(async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      if (profileData) {
        setProfile(profileData);
      }
    }
  }, [user, fetchProfile]);

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchProfile(session.user.id).then((profileData) => {
            setProfile(profileData);
            syncThemeFromProfile(profileData);
          });
        }

        setInitialized(true);
      })
      .catch((error) => {
        // Log error but still initialize to prevent infinite loading
        console.error('Failed to get session:', error);
        setInitialized(true);
      });

    // Listen for auth changes.
    // IMPORTANT: This callback must be synchronous (non-async). Supabase
    // runs it inside an exclusive lock. If we `await` any Supabase API call
    // (like fetchProfile → supabase.from().select()), that call tries to
    // acquire the same lock → deadlock. Using .then() lets the callback
    // return immediately, releasing the lock before the profile fetch runs.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // After explicit signOut, Supabase may fire INITIAL_SESSION with a
      // phantom session from its in-memory cache. Suppress it so we don't
      // re-authenticate the user.
      if (signedOutRef.current && event === 'INITIAL_SESSION' && session) {
        return;
      }

      // Reset flag when user actually signs in
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        signedOutRef.current = false;
      }

      setSession(session);
      setUser(session?.user ?? null);

      // Mark as initialized if we receive an auth event
      // This handles the case where onAuthStateChange fires before getSession() completes
      // (e.g., returning from Stripe portal with session in URL)
      setInitialized(true);

      if (session?.user) {
        // Fetch profile outside the lock via .then() — never await here
        fetchProfile(session.user.id).then((profileData) => {
          if (profileData) {
            setProfile(profileData);
            syncThemeFromProfile(profileData);
          }
          // If fetchProfile fails, keep the existing profile in state
        });
      } else {
        setProfile(null);
      }

      if (event === 'SIGNED_OUT') {
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, syncThemeFromProfile]);

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    fullName?: string
  ): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      // Handle email sending errors - user may still be created
      if (error) {
        const errorMessage = error.message.toLowerCase();

        // If email failed to send but user was likely created, treat as partial success
        if (errorMessage.includes('email') && errorMessage.includes('send')) {
          return {
            success: true,
            error: 'Account created, but we had trouble sending the confirmation email. Please try logging in or contact support.',
          };
        }

        return { success: false, error: formatAuthError(error) };
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        return {
          success: true,
          error: 'Please check your email to confirm your account.',
        };
      }

      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const signIn = async (
    email: string,
    password: string
  ): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: formatAuthError(error) };
      }

      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with magic link (passwordless)
  const signInWithMagicLink = async (email: string): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        return { success: false, error: formatAuthError(error) };
      }

      return {
        success: true,
        error: 'Check your email for the magic link!',
      };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google OAuth
  const signInWithGoogle = async (): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        return { success: false, error: formatAuthError(error) };
      }

      // OAuth redirects, so we won't reach this
      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // Sign out — lets Supabase handle its own cleanup properly.
  // Supabase signOut clears localStorage, stops refresh timer, clears
  // internal session, and fires SIGNED_OUT event.
  // Caller should follow with window.location.replace('/') for a clean reload.
  const signOut = async () => {
    signedOutRef.current = true;
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // If signOut fails, manually clean up localStorage as fallback
      try {
        Object.keys(localStorage)
          .filter((key) => key.startsWith('sb-'))
          .forEach((key) => localStorage.removeItem(key));
      } catch {
        // ignore
      }
    }
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  // Request password reset
  const resetPassword = async (email: string): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { success: false, error: formatAuthError(error) };
      }

      return {
        success: true,
        error: 'Check your email for the password reset link.',
      };
    } finally {
      setLoading(false);
    }
  };

  // Update password (after reset)
  const updatePassword = async (newPassword: string): Promise<AuthResult> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: formatAuthError(error) };
      }

      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    initialized,
    signUp,
    signIn,
    signInWithMagicLink,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Format Supabase auth errors into user-friendly messages
function formatAuthError(error: AuthError): string {
  const message = error.message.toLowerCase();

  if (message.includes('invalid login credentials')) {
    return 'Invalid email or password. Please try again.';
  }
  if (message.includes('email not confirmed')) {
    return 'Please check your email and confirm your account.';
  }
  if (message.includes('user already registered')) {
    return 'An account with this email already exists.';
  }
  if (message.includes('password')) {
    return 'Password must be at least 6 characters long.';
  }
  if (message.includes('rate limit')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (message.includes('network')) {
    return 'Network error. Please check your connection.';
  }

  return error.message;
}
